import React, { useMemo, useState } from 'react';
import { format, utcToZonedTime } from 'date-fns-tz';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '@epoch-ui/lib/utils';
import { Icon } from '@epoch-ui/components/Icon';
import { Badge, badgeVariants } from '@epoch-ui/components/Badge';
import {
  COMMUNICATION_REMINDER_MEDIUM,
  NormalizedReminder
} from '@event-editor/tabs/EditEventCommunications/types/communication.types';
import { REMINDER_STATUS, REMINDERS_GUEST_LIST } from 'constants/enums';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@epoch-ui/components/DropdownMenu';
import { Button } from '@epoch-ui/components/Button';
import { parseUTCDate } from 'utils/dateTimeUtils';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { SlackPreviewReminderModal } from '@event-editor/components/CommunicationPreview/SlackPreviewReminderModal';
import { EmailPreviewReminderModal } from '@event-editor/components/CommunicationPreview/EmailPreviewReminderModal';
/**
 * Badge mapping for notification statuses
 * Note: There are other statuses that are not mapped here, eg; SENDING, etc.
 */
const REMINDER_STATUS_BADGES: Record<
  number,
  {
    variant: VariantProps<typeof badgeVariants>['variant'];
    text: string;
  }
> = {
  [REMINDER_STATUS.SENT]: { variant: 'positive', text: 'Sent' },
  [REMINDER_STATUS.SCHEDULED]: { variant: 'secondary', text: 'Scheduled' },
  [REMINDER_STATUS.FAILED]: { variant: 'destructive', text: 'Failed' },
  [REMINDER_STATUS.SENDING]: { variant: 'primary', text: 'Sending' },
  [REMINDER_STATUS.CREATING_MESSAGES]: { variant: 'primary', text: 'Sending' }
};

const DEFAULT_BADGE = { variant: 'secondary', text: 'Unknown' } as const;

interface ReminderListCardProps {
  normalizedReminder: NormalizedReminder;
  openUpdateEventCommunicationSideSheet: (
    id: number,
    medium: COMMUNICATION_REMINDER_MEDIUM
  ) => void;
  disabled?: boolean;
}

/**
 * Render the reminder list card.
 * Given the normalized reminder, render the list card.
 * @param normalizedReminder - The normalized reminder object.
 * @param openUpdateEventCommunicationSideSheet - The function to open the update event communication side sheet.
 * @returns
 */
export const ReminderListCard: React.FC<ReminderListCardProps> = ({
  normalizedReminder,
  openUpdateEventCommunicationSideSheet,
  disabled = false
}) => {
  const badgeConfig = REMINDER_STATUS_BADGES[normalizedReminder.notifyStatus] || DEFAULT_BADGE;
  const { timezoneCode } = useEditableEvent();
  const [showSlackPreviewModal, setShowSlackPreviewModal] = useState(false);
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);

  /**
   * Get the target status text
   * 'selectedGuestStatuses' from backend returns mapped numeric values of guest statuses.
   * eg; "To: Going, Incomplete"
   */
  const targetStatusText = useMemo(() => {
    // Map numeric guest status values to their text representations using REMINDERS_GUEST_LIST
    const statusTexts = normalizedReminder.selectedGuestStatuses
      .map((statusNumber: number) => {
        // Find the text key that corresponds to this numeric value
        const statusText = Object.keys(REMINDERS_GUEST_LIST).find(
          (key) => REMINDERS_GUEST_LIST[key as keyof typeof REMINDERS_GUEST_LIST] === statusNumber
        );
        return statusText || `Unknown`;
      })
      .join(', ');

    return `To: ${statusTexts}`;
  }, [normalizedReminder.selectedGuestStatuses]);

  /**
   * Format the datetime in the timezone
   * Get the time text
   * eg; "5:00 AM PDT"
   */
  const timeText = useMemo(() => {
    const tzCode = timezoneCode || 'UTC';
    return format(
      utcToZonedTime(parseUTCDate(normalizedReminder.datetime), tzCode),
      'MMM d, yyyy, h:mm aa z',
      {
        timeZone: tzCode
      }
    );
  }, [normalizedReminder.datetime, timezoneCode]);

  const handlePreview = (medium: COMMUNICATION_REMINDER_MEDIUM): void => {
    if (medium === COMMUNICATION_REMINDER_MEDIUM.SLACK) {
      setShowSlackPreviewModal(true);
      return;
    }
    if (medium === COMMUNICATION_REMINDER_MEDIUM.EMAIL) {
      setShowEmailPreviewModal(true);
    }
  };

  const handleOpenUpdateEventCommunicationSideSheet = (): void => {
    openUpdateEventCommunicationSideSheet(normalizedReminder.id, normalizedReminder.medium);
  };

  return (
    <>
      <div
        className={cn('tw-flex tw-items-center tw-justify-between', disabled && 'tw-opacity-40')}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon name="message-square-text" size={20} className="tw-text-medium-grey" />
          <button
            type="button"
            disabled={disabled}
            className="tw-ml-4 tw-flex tw-flex-col tw-items-start tw-gap-1 tw-p-0"
            onClick={() =>
              normalizedReminder.notifyStatus === REMINDER_STATUS.SCHEDULED
                ? handleOpenUpdateEventCommunicationSideSheet()
                : handlePreview(normalizedReminder.medium)
            }
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              <p className="tw-m-0 tw-font-medium tw-leading-4">{normalizedReminder.title}</p>
            </div>

            <p className="tw-m-0 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
              {targetStatusText} • {timeText} • {normalizedReminder.medium}
            </p>
          </button>
        </div>
        {/* If sent, only show preview button */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <Badge variant={badgeConfig.variant}>{badgeConfig.text}</Badge>
          {normalizedReminder.notifyStatus === REMINDER_STATUS.SCHEDULED ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  disabled={disabled}
                  className="tw-h-6 tw-w-6 tw-p-0 disabled:tw-opacity-100"
                >
                  <Icon name="ellipsis-vertical" size={16} className="tw-text-medium-grey" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="tw-w-32">
                <DropdownMenuItem>
                  <Button
                    onClick={() => handlePreview(normalizedReminder.medium)}
                    variant="ghost"
                    disabled={disabled}
                    className="tw-h-4 tw-p-0 tw-text-xs disabled:tw-opacity-100"
                  >
                    <Icon name="eye" size={16} className="tw-mr-2" />
                    Preview
                  </Button>
                </DropdownMenuItem>

                <DropdownMenuItem>
                  <Button
                    onClick={handleOpenUpdateEventCommunicationSideSheet}
                    variant="ghost"
                    disabled={disabled}
                    className="tw-h-4 tw-p-0 tw-text-xs disabled:tw-opacity-100"
                  >
                    <Icon name="pencil" size={16} className="tw-mr-2" />
                    Edit
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => handlePreview(normalizedReminder.medium)}
              variant="ghost"
              className="tw-h-6 tw-w-6 tw-p-0 "
            >
              <Icon name="eye" size={16} />
            </Button>
          )}
        </div>
      </div>
      {normalizedReminder.medium === COMMUNICATION_REMINDER_MEDIUM.SLACK && (
        <SlackPreviewReminderModal
          open={showSlackPreviewModal}
          onOpenChange={setShowSlackPreviewModal}
          message={normalizedReminder.message}
        />
      )}
      {normalizedReminder.medium === COMMUNICATION_REMINDER_MEDIUM.EMAIL && (
        <EmailPreviewReminderModal
          open={showEmailPreviewModal}
          onOpenChange={setShowEmailPreviewModal}
          message={normalizedReminder.message}
          title={normalizedReminder.title}
        />
      )}
    </>
  );
};
